import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { nanoid } from 'nanoid';

export interface Position {
  ticker: string;
  qty: number;
  avgPrice: number;
}

export interface Order {
  id: string;
  ticker: string;
  side: 'buy' | 'sell';
  type: 'market' | 'limit' | 'stop' | 'trail';
  qty: number;
  limitPrice?: number;
  stopPrice?: number;
  trailPct?: number;     // e.g. 0.05 = 5% trailing distance
  trailPeak?: number;    // updated on tick: max price seen for sell trail, min for buy trail
  createdAt: number;
}

export interface Fill {
  ticker: string;
  side: 'buy' | 'sell';
  qty: number;
  price: number;
  at: number;
}

interface MarketsStore {
  cash: number;
  positions: Position[];
  openOrders: Order[];
  realizedPnl: number;
  recentFills: Fill[];
  activeTickerId: string;
  equityATH: number;       // all-time peak of (cash + market value of positions); ratchets up on tick
  setActiveTicker: (ticker: string) => void;
  addCash: (delta: number) => void;
  applyFill: (ticker: string, side: 'buy' | 'sell', qty: number, price: number) => void;
  placeOrder: (order: Omit<Order, 'id' | 'createdAt'>) => string;
  cancelOrder: (id: string) => void;
  updateTrailPeak: (id: string, peak: number) => void;
  bumpEquityATH: (equity: number) => boolean;  // returns true if a new ATH was set
}

export const useMarkets = create<MarketsStore>()(
  immer((set) => ({
    cash: 5_000,
    positions: [],
    openOrders: [],
    realizedPnl: 0,
    recentFills: [],
    activeTickerId: 'TARI',
    equityATH: 0,
    setActiveTicker: (ticker) => set((s) => { s.activeTickerId = ticker; }),
    addCash: (delta) => set((s) => { s.cash += delta; }),
    applyFill: (ticker, side, qty, price) => set((s) => {
      const total = qty * price;
      if (side === 'buy') {
        s.cash -= total;
        const existing = s.positions.find((p) => p.ticker === ticker);
        if (existing) {
          const newQty = existing.qty + qty;
          existing.avgPrice = (existing.avgPrice * existing.qty + price * qty) / newQty;
          existing.qty = newQty;
        } else {
          s.positions.push({ ticker, qty, avgPrice: price });
        }
      } else {
        s.cash += total;
        const existing = s.positions.find((p) => p.ticker === ticker);
        if (existing) {
          const closeQty = Math.min(qty, existing.qty);
          const pnl = (price - existing.avgPrice) * closeQty;
          s.realizedPnl += pnl;
          existing.qty -= closeQty;
          if (existing.qty <= 0) {
            s.positions = s.positions.filter((p) => p.ticker !== ticker);
          }
          // Big-win / big-loss stinger
          if (Math.abs(pnl) > 1000) {
            queueMicrotask(() => {
              import('@/audio/synth').then((m) => {
                if (pnl > 0) m.sounds.bigWin();
                else m.sounds.bigLoss();
              });
              import('@/components/PnlFlash').then((m) => {
                m.usePnlFlash.getState().push(pnl, ticker);
              });
              // Outsized realized P&L: one NPC reacts on Y. Threshold tuned to
              // not spam — fires only on five-figure-plus moves.
              if (Math.abs(pnl) >= 10_000) {
                import('@/engine/tradeReactions').then((m) => m.reactToTrade(ticker, pnl));
              }
            });
          }
        }
      }
      s.recentFills.unshift({ ticker, side, qty, price, at: Date.now() });
      if (s.recentFills.length > 50) s.recentFills.pop();
    }),
    placeOrder: (order) => {
      const id = nanoid();
      set((s) => {
        s.openOrders.push({ ...order, id, createdAt: Date.now() });
      });
      return id;
    },
    cancelOrder: (id) => set((s) => {
      s.openOrders = s.openOrders.filter((o) => o.id !== id);
    }),
    updateTrailPeak: (id, peak) => set((s) => {
      const o = s.openOrders.find((x) => x.id === id);
      if (o) o.trailPeak = peak;
    }),
    bumpEquityATH: (equity) => {
      let isNew = false;
      set((s) => {
        if (equity > s.equityATH * 1.0005) {  // 0.05% hysteresis to avoid jittery audio
          s.equityATH = equity;
          isNew = true;
        }
      });
      return isNew;
    },
  })),
);
